/* eslint-disable static-service/require-stories -- context provider that is covered by tests*/
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";

import {DEFAULT_SORT_ORDER} from "./constants.ts";
import type {Column, Sort} from "./types.ts";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";
import caretUpIcon from "@phosphor-icons/core/bold/caret-up-bold.svg";

interface Props<T> {
    /**
     * The column to render.
     */
    column: Column<T>;
    /**
     * Callback when the column is sorted.
     */
    onSort: (nextSort: Sort) => void;
    /**
     * The current sort state.
     */
    sort: Sort;
    /**
     * Custom icons to use for sorting.
     */
    sortIcons?: {
        ascending: PhosphorBold | PhosphorFill | PhosphorRegular;
        descending: PhosphorBold | PhosphorFill | PhosphorRegular;
    };
}

/**
 * Renders a sortable cell in the table header.
 */
export function SortableHeader<T>({
    column,
    onSort,
    sort,
    sortIcons,
}: Props<T>): React.ReactElement {
    const isBeingSorted = sort.orderBy === column.dataKey;
    const sortOrder = isBeingSorted
        ? sort.order
        : (column.sortable ?? DEFAULT_SORT_ORDER);

    return (
        <Clickable
            onClick={() => {
                onSort({orderBy: column.dataKey ?? "", order: sortOrder});
            }}
            style={[styles.sortable, isBeingSorted && styles.sortableActive]}
            hideDefaultFocusRing={true}
        >
            {({focused, hovered}) => (
                <>
                    {column.label}

                    {isBeingSorted || hovered || focused ? (
                        <>
                            <Strut size={8} />
                            <PhosphorIcon
                                color={semanticColor.icon.primary}
                                icon={
                                    sortOrder === "ascending"
                                        ? sortIcons
                                            ? sortIcons.ascending
                                            : caretDownIcon
                                        : sortIcons
                                          ? sortIcons.descending
                                          : caretUpIcon
                                }
                                size="small"
                                aria-hidden={true}
                                testId={`sort-icon-${sortOrder}`}
                                style={{paddingBlockStart: sizing.size_080}}
                            />
                        </>
                    ) : (
                        // If the sorting icon is not shown, add equivalent empty
                        // space. This prevents the column width from resizing
                        // on hover if there the header is the longest text in
                        // the column.
                        <View style={{width: sizing.size_240}} />
                    )}
                </>
            )}
        </Clickable>
    );
}

const styles = StyleSheet.create({
    sortable: {
        display: "inline-flex",
        flexWrap: "wrap",
        marginInlineStart: sizing.size_020,
        padding: sizing.size_040,
        textTransform: "uppercase",
        ":hover": {
            backgroundColor: semanticColor.border.primary,
        },
        // Only show the focus ring when the user is navigating with the
        // keyboard.
        ":focus-visible": {
            borderRadius: sizing.size_020,
            outline: `solid 1 ${semanticColor.focus.outer}`,
        },
        ":active": {
            backgroundColor: semanticColor.border.primary,
        },
    },
    sortableActive: {
        fontWeight: "70rem",
    },
});
